/**
 * Member-support panel — Phase A4 deliverable.
 *
 * Renders the auth/entitlement/access-state side of a subscriber for
 * operators handling "why am I locked out" / "why can't I see this
 * issue" pilot-launch tickets. Backend is /api/admin/members/{id};
 * the panel sits below the existing /api/subscribers/{id} sections
 * on SubscriberDetail so operators see ONE record-per-person.
 *
 * Views:
 *   - Per-publication entitlement decisions (the EntitlementDecision
 *     reason taxonomy, including "why denied" for non-active rows).
 *   - 24-hour auth-attempt counters (login / claim / reset / magic
 *     link / failed) — answers "why is this user rate-limited".
 *   - Active temp-access grants.
 *   - Recent reads (last 20 content_impressions).
 *
 * Actions: reset rate limits, and editing the expiry on entitlement rows
 * backed by a membership we sold ourselves (SimpleCirc-sourced rows stay
 * read-only — see ExpiryCell). Send-magic-link and grant-temp-access
 * ship in a follow-up — both have email/billing implications that
 * warrant explicit confirmation flows.
 */
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ApiError, api,
  chargeRenewalNow,
  getMemberRenewals,
  sendCardUpdateLink,
  stopAutoRenew,
  type AuditResponse,
  type EntitlementSnapshot,
  type CardOnFile,
  type MemberAdminDetail,
  type RecentReadsResponse,
  type RenewalSubscription,
} from '../api/client'
import { Panel } from '../components/Panel'
import { PubBadge } from '../components/PubBadge'
import { Button } from '../components/Button'
import { AccountChangeLogPanel } from './AccountChangeLogPanel'
import { fmtCardExpiry, fmtDateTime, fmtMoney, fmtRelative, publicationName } from '../lib/format'
import { renewalPrice } from '../lib/renewalPrice'

interface Props {
  subscriberId: number
}

export function MemberSupportPanel({ subscriberId }: Props) {
  const qc = useQueryClient()
  const [showAudit, setShowAudit] = useState(false)
  const [showReads, setShowReads] = useState(false)
  const [showChanges, setShowChanges] = useState(false)

  const detail = useQuery({
    queryKey: ['admin-member', subscriberId],
    queryFn: () => api.get<MemberAdminDetail>(`/api/admin/members/${subscriberId}`),
  })

  const audit = useQuery({
    queryKey: ['admin-member-audit', subscriberId],
    queryFn: () => api.get<AuditResponse>(`/api/admin/members/${subscriberId}/audit?limit=20`),
    enabled: showAudit,
  })

  const reads = useQuery({
    queryKey: ['admin-member-reads', subscriberId],
    queryFn: () => api.get<RecentReadsResponse>(`/api/admin/members/${subscriberId}/recent-reads?limit=20`),
    enabled: showReads,
  })

  const resetRate = useMutation({
    mutationFn: () =>
      api.post(`/api/admin/members/${subscriberId}/reset-rate-limit`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-member', subscriberId] })
    },
  })

  if (detail.isLoading) {
    return (
      <Panel label="Member access (loading…)">
        <div className="px-4 py-6 label text-paper-trace">Loading…</div>
      </Panel>
    )
  }
  if (detail.isError) {
    const err = detail.error instanceof ApiError ? detail.error.message : 'failed'
    return (
      <Panel label="Member access" accent="red">
        <div className="px-4 py-4 label text-radar-red">{err}</div>
      </Panel>
    )
  }
  const m = detail.data!
  const counts = m.auth_attempts_24h
  const totalAttempts =
    counts.login_24h + counts.claim_24h + counts.reset_24h + counts.magic_link_24h +
    counts.sign_in_code_24h
  const activeGrants = m.temp_access_grants.filter((g) => g.is_active)

  return (
    <div className="space-y-6">
      <Panel
        label="Member access"
        accent="amber"
        right={
          <Link
            to={`/offline-intake?subscriber=${subscriberId}`}
            className="label-amber hover:underline"
          >
            Record a check →
          </Link>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-4">
          {/* Account state column */}
          <div className="space-y-2">
            <h3 className="label text-paper-dim mb-2">Account state</h3>
            <KV k="Claimed" v={fmtDateTime(m.claimed_at) || 'no'} />
            <KV k="Email verified" v={fmtDateTime(m.email_verified_at) || 'no'} />
            <KV k="Has password" v={m.has_password ? 'yes' : 'no'} />
            <KV k="Last synced" v={m.last_synced_at ? fmtRelative(m.last_synced_at) : 'never'} />
            {m.merged_into_id && (
              <div className="font-mono text-[12px] text-radar-amber">
                merged → #{m.merged_into_id}
              </div>
            )}
          </div>

          {/* Auth attempts column */}
          <div className="space-y-2">
            <div className="flex items-center justify-between mb-2">
              <h3 className="label text-paper-dim">Auth attempts (24h)</h3>
              <Button
                variant="outline"
                size="sm"
                disabled={resetRate.isPending || totalAttempts === 0}
                onClick={() => resetRate.mutate()}
                title="Clear rate-limit counters for this subscriber"
              >
                {resetRate.isPending ? 'Resetting…' : 'Reset rate limit'}
              </Button>
            </div>
            <KV k="Login" v={counts.login_24h.toString()} />
            <KV k="Claim" v={counts.claim_24h.toString()} />
            <KV k="Reset" v={counts.reset_24h.toString()} />
            <KV k="Magic link" v={counts.magic_link_24h.toString()} />
            <KV k="Wrong codes" v={counts.sign_in_code_24h.toString()} />
            <KV k="Failed" v={counts.failed_24h.toString()} tone={counts.failed_24h > 5 ? 'amber' : undefined} />
            {resetRate.isSuccess && (
              <div className="font-mono text-[10px] text-radar-green">✓ counters cleared</div>
            )}
          </div>

          {/* Temp access column */}
          <div className="space-y-2">
            <h3 className="label text-paper-dim mb-2">
              Temp access ({activeGrants.length} active)
            </h3>
            {activeGrants.length === 0 && (
              <div className="font-mono text-[12px] text-paper-trace italic">
                no active grants
              </div>
            )}
            {activeGrants.map((g) => (
              <div key={g.id} className="font-mono text-[12px] text-paper-dim border-l-2 border-radar-amber pl-2">
                <div>
                  <PubBadge slug={g.publication_slug} />{' '}
                  until{' '}
                  <span className="tabular-nums">
                    {g.expires_at ? fmtDateTime(g.expires_at) : 'never'}
                  </span>
                </div>
                <div className="text-paper-trace text-[11px]">{g.reason}</div>
              </div>
            ))}
          </div>
        </div>
      </Panel>

      {/* Per-pub entitlement table — the "why is access denied" view */}
      <Panel label="Entitlement per publication" bodyClassName="p-0">
        {m.entitlements.length === 0 ? (
          <div className="px-4 py-6 label text-paper-trace">
            No publications subscribed
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-ink-500">
                <Th>Publication</Th>
                <Th>Entitled?</Th>
                <Th>Reason</Th>
                <Th>Expires</Th>
                <Th>Grace until</Th>
                <Th>Notes</Th>
              </tr>
            </thead>
            <tbody>
              {m.entitlements.map((e) => (
                <tr key={e.publication_slug} className="border-b border-ink-500/40">
                  <Td><PubBadge slug={e.publication_slug} /></Td>
                  <Td>
                    <span
                      className={
                        'label ' +
                        (e.entitled ? 'text-radar-green' : 'text-radar-red')
                      }
                    >
                      {e.entitled ? 'YES' : 'NO'}
                    </span>
                  </Td>
                  <Td><span className="font-mono text-[12px]">{e.reason}</span></Td>
                  <Td>
                    <ExpiryCell subscriberId={subscriberId} entitlement={e} />
                  </Td>
                  <Td><span className="font-mono text-[12px] tabular-nums">{e.grace_until ?? '—'}</span></Td>
                  <Td><span className="font-mono text-[11px] text-paper-dim">{e.notes ?? ''}</span></Td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <RenewalsCard subscriberId={subscriberId} />

      {/* Optional: audit + recent reads — collapsed by default to keep
          the page short. Operators expand when investigating. */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowAudit((v) => !v)}
        >
          {showAudit ? 'Hide' : 'Show'} sign-in log
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowChanges((v) => !v)}
        >
          {showChanges ? 'Hide' : 'Show'} account update log
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => setShowReads((v) => !v)}
        >
          {showReads ? 'Hide' : 'Show'} recent reads
        </Button>
      </div>

      {showAudit && (
        <Panel label="Sign-in log (last 20)" bodyClassName="p-0">
          {audit.isLoading ? (
            <div className="px-4 py-4 label text-paper-trace">Loading…</div>
          ) : audit.data?.entries.length === 0 ? (
            <div className="px-4 py-4 label text-paper-trace">No entries</div>
          ) : (
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-ink-500">
                  <Th>When</Th>
                  <Th>Event</Th>
                  <Th>IP</Th>
                  <Th>Details</Th>
                </tr>
              </thead>
              <tbody>
                {audit.data?.entries.map((e) => (
                  <tr key={e.id} className="border-b border-ink-500/40">
                    <Td><span className="font-mono">{fmtDateTime(e.created_at)}</span></Td>
                    <Td><span className="font-mono">{e.event}</span></Td>
                    <Td><span className="font-mono">{e.ip ?? '—'}</span></Td>
                    <Td>
                      <span className="font-mono text-[11px] text-paper-dim">
                        {Object.keys(e.details).length > 0
                          ? JSON.stringify(e.details)
                          : '—'}
                      </span>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}

      {showChanges && <AccountChangeLogPanel subscriberId={subscriberId} />}

      {showReads && (
        <Panel label="Recent reads (last 20)" bodyClassName="p-0">
          {reads.isLoading ? (
            <div className="px-4 py-4 label text-paper-trace">Loading…</div>
          ) : reads.isError ? (
            // 403 when member reading history is switched off or this
            // account lacks the member-activity permission.
            <div className="px-4 py-4 label text-paper-trace">Reading history isn't available to your account</div>
          ) : reads.data?.entries.length === 0 ? (
            <div className="px-4 py-4 label text-paper-trace">No recent reads</div>
          ) : (
            <table className="w-full text-[12px]">
              <thead>
                <tr className="border-b border-ink-500">
                  <Th>When</Th>
                  <Th>Article</Th>
                  <Th>Pub</Th>
                  <Th>Channel</Th>
                </tr>
              </thead>
              <tbody>
                {reads.data?.entries.map((e, i) => (
                  <tr key={`${e.article_id}-${i}`} className="border-b border-ink-500/40">
                    <Td><span className="font-mono">{fmtDateTime(e.created_at)}</span></Td>
                    <Td>{e.article_title ?? <span className="text-paper-trace italic">(no title)</span>}</Td>
                    <Td>{e.publication_slug ? <PubBadge slug={e.publication_slug} /> : '—'}</Td>
                    <Td><span className="font-mono">{e.channel}</span></Td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Panel>
      )}
    </div>
  )
}

// ---- Renewals — card on file, attempts, charge now, send card link -

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

function RenewalsCard({ subscriberId }: { subscriberId: number }) {
  const qc = useQueryClient()
  const [rowErrors, setRowErrors] = useState<Record<number, string>>({})
  const [rowNotices, setRowNotices] = useState<Record<number, string>>({})

  const renewals = useQuery({
    queryKey: ['admin-member-renewals', subscriberId],
    queryFn: () => getMemberRenewals(subscriberId),
  })

  const invalidate = () =>
    qc.invalidateQueries({ queryKey: ['admin-member-renewals', subscriberId] })

  const errorMessage = (err: unknown): string =>
    err instanceof ApiError
      ? typeof err.detail === 'object' && err.detail !== null && 'detail' in (err.detail as object)
        ? String((err.detail as { detail: unknown }).detail)
        : err.message
      : 'request failed'

  const chargeNow = useMutation({
    mutationFn: (subscriptionId: number) => chargeRenewalNow(subscriberId, subscriptionId),
    onSuccess: (_data, subscriptionId) => {
      setRowErrors((prev) => { const next = { ...prev }; delete next[subscriptionId]; return next })
      setRowNotices((prev) => ({ ...prev, [subscriptionId]: 'attempt made' }))
      invalidate()
    },
    onError: (err, subscriptionId) => {
      setRowErrors((prev) => ({ ...prev, [subscriptionId]: errorMessage(err) }))
    },
  })

  const sendLink = useMutation({
    mutationFn: (subscriptionId: number) => sendCardUpdateLink(subscriberId, subscriptionId),
    onSuccess: (_data, subscriptionId) => {
      setRowErrors((prev) => { const next = { ...prev }; delete next[subscriptionId]; return next })
      setRowNotices((prev) => ({ ...prev, [subscriptionId]: 'card-update link sent' }))
    },
    onError: (err, subscriptionId) => {
      setRowErrors((prev) => ({ ...prev, [subscriptionId]: errorMessage(err) }))
    },
  })

  const stopRenew = useMutation({
    mutationFn: ({ subscriptionId, reason }: { subscriptionId: number; reason: string }) =>
      stopAutoRenew(subscriberId, subscriptionId, reason),
    onSuccess: (data, { subscriptionId }) => {
      setRowErrors((prev) => { const next = { ...prev }; delete next[subscriptionId]; return next })
      setRowNotices((prev) => ({
        ...prev,
        [subscriptionId]: data.current_period_end
          ? `auto-renew stopped · access runs to ${data.current_period_end}`
          : 'auto-renew stopped',
      }))
      invalidate()
    },
    onError: (err, { subscriptionId }) => {
      setRowErrors((prev) => ({ ...prev, [subscriptionId]: errorMessage(err) }))
    },
  })

  if (renewals.isLoading) {
    return (
      <Panel label="Renewals (loading…)">
        <div className="px-4 py-6 label text-paper-trace">Loading…</div>
      </Panel>
    )
  }
  if (renewals.isError) {
    return (
      <Panel label="Renewals" accent="red">
        <div className="px-4 py-4 label text-radar-red">
          {renewals.error instanceof ApiError ? renewals.error.message : 'failed'}
        </div>
      </Panel>
    )
  }

  const subs = renewals.data?.subscriptions ?? []
  if (subs.length === 0) {
    return (
      <Panel label="Renewals">
        <div className="px-4 py-6 label text-paper-trace">
          No subscriptions billed by us or PayPal
        </div>
      </Panel>
    )
  }

  return (
    <Panel label="Renewals" bodyClassName="p-0">
      <div className="divide-y divide-ink-500/60">
        {subs.map((s) => (
          <RenewalSubRow
            key={s.subscription_id}
            sub={s}
            error={rowErrors[s.subscription_id]}
            notice={rowNotices[s.subscription_id]}
            chargingNow={chargeNow.isPending && chargeNow.variables === s.subscription_id}
            sendingLink={sendLink.isPending && sendLink.variables === s.subscription_id}
            stopping={stopRenew.isPending && stopRenew.variables?.subscriptionId === s.subscription_id}
            onChargeNow={() => {
              const { amount } = renewalPrice(s)
              const price = amount != null ? fmtMoney(amount, false, 2) : 'the renewal amount'
              const last4 = s.card_on_file?.last4 ?? 'on file'
              if (window.confirm(`Charge ${price} to the card ending ${last4} now?`)) {
                chargeNow.mutate(s.subscription_id)
              }
            }}
            onSendCardLink={() => sendLink.mutate(s.subscription_id)}
            onStopAutoRenew={() => {
              const who = s.billing_provider === 'paypal' ? 'PayPal' : 'our renewal engine'
              const until = s.current_period_end ?? 'the end of the paid period'
              // A BOGO pair: this row's PayPal agreement also bills one or
              // more OTHER memberships. PayPal has no per-row cancel, so
              // this ends every one of them too — named here, before the
              // prompt, since a cancelled PayPal agreement cannot be undone.
              const bogoNote = s.bogo_partner_memberships.length > 0
                ? ` This PayPal agreement also bills, and will also end: ${s.bogo_partner_memberships.join(', ')}.`
                : ''
              const reason = window.prompt(
                `Stop auto-renew? ${who} will not bill this membership again; access runs to ${until}.` +
                `${bogoNote} ` +
                'Reason (required, kept in the audit log):',
              )
              if (reason && reason.trim()) {
                stopRenew.mutate({ subscriptionId: s.subscription_id, reason: reason.trim() })
              }
            }}
          />
        ))}
      </div>
    </Panel>
  )
}

function RenewalSubRow({
  sub, error, notice, chargingNow, sendingLink, stopping, onChargeNow, onSendCardLink, onStopAutoRenew,
}: {
  sub: RenewalSubscription
  error?: string
  notice?: string
  chargingNow: boolean
  sendingLink: boolean
  stopping: boolean
  onChargeNow: () => void
  onSendCardLink: () => void
  onStopAutoRenew: () => void
}) {
  // PayPal holds the funding source and runs the charge itself; there is
  // no card here to charge or replace, only an agreement to cancel.
  const isPayPal = sub.billing_provider === 'paypal'
  const hasCard = !!sub.card_on_file?.last4
  const isDue = !!sub.current_period_end && sub.current_period_end <= todayStr()
  // SimpleCirc bills this membership until the cutover. Charging it here,
  // or inviting the member to change the card we would charge, double-bills
  // them, so the API refuses both with a 409.
  const scManaged = sub.sc_managed
  // Over (a refund that ended access, a lapse): the engine will never
  // bill it again, so there is no renewal date and nothing to act on.
  const ended = sub.membership_ended
  const price = renewalPrice(sub)
  const canChargeNow = isDue && hasCard && !scManaged && !isPayPal && !ended
  const renewOff = !sub.auto_renew && sub.do_not_renew
  const canStop = !scManaged && !renewOff

  return (
    <div className="p-4 space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="space-y-1">
          <div className="label text-paper-trace">Publication</div>
          <div className="font-mono text-[13px] text-paper">
            {publicationName(sub.publication_slug)}
          </div>
          <div className="font-mono text-[11px] text-paper-trace">{sub.billing_plan ?? '—'}</div>
          {scManaged && (
            <div className="font-mono text-[10px] text-radar-amber">
              SimpleCirc-managed until cutover
            </div>
          )}
        </div>
        <div className="space-y-1">
          <div className="label text-paper-trace">{ended ? 'Ended' : 'Renews'}</div>
          <div className="font-mono text-[13px] tabular-nums text-paper">
            {sub.current_period_end ?? '—'}
          </div>
          <div className="font-mono text-[11px] text-paper-trace">
            {fmtMoney(price.amount, false, 2)} · auto-renew {sub.auto_renew ? 'on' : 'off'}
            {sub.do_not_renew && ' · do-not-renew'}
          </div>
          {price.note && (
            <div className="font-mono text-[11px] text-paper-trace">{price.note}</div>
          )}
        </div>
        <div className="space-y-1">
          <div className="label text-paper-trace">{isPayPal ? 'Billed by' : 'Card on file'}</div>
          {isPayPal ? (
            <div className="font-mono text-[13px] text-paper">
              PayPal
              <span className="text-paper-trace"> · {sub.paypal_subscription_id ?? '—'}</span>
            </div>
          ) : (
            <CardExpiryCell card={sub.card_on_file} />
          )}
          <div className="font-mono text-[11px] text-paper-trace">
            {isPayPal ? 'PayPal runs the charge and its own retries' : (sub.auto_renew_status ?? '—')}
          </div>
        </div>
        <div className="space-y-2 flex flex-col items-start md:items-end">
          {ended ? (
            <div className="font-mono text-[11px] text-paper-trace md:text-right">
              Membership {sub.status === 'expired' ? 'expired' : 'cancelled'} · nothing will renew
            </div>
          ) : (
          <>
          {!isPayPal && (
            <>
              <Button
                variant="outline"
                size="sm"
                disabled={!canChargeNow || chargingNow}
                onClick={onChargeNow}
                title={
                  scManaged ? 'SimpleCirc bills this membership until the cutover'
                  : !hasCard ? 'No card on file'
                  : !isDue ? 'Not due yet'
                  : 'Retry the renewal now'
                }
              >
                {chargingNow ? 'Charging…' : 'Charge now'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={sendingLink || scManaged}
                onClick={onSendCardLink}
                title={scManaged ? 'SimpleCirc bills this membership until the cutover' : undefined}
              >
                {sendingLink ? 'Sending…' : 'Send card-update link'}
              </Button>
            </>
          )}
          <Button
            variant="outline"
            size="sm"
            disabled={!canStop || stopping}
            onClick={onStopAutoRenew}
            title={
              scManaged ? 'SimpleCirc bills this membership until the cutover'
              : renewOff ? 'Auto-renew is already off'
              : isPayPal ? 'Cancel the PayPal agreement; access runs to the paid-through date'
              : 'Stop charging the card; access runs to the paid-through date'
            }
          >
            {stopping ? 'Stopping…' : renewOff ? 'Auto-renew off' : 'Stop auto-renew'}
          </Button>
          </>
          )}
        </div>
      </div>

      {error && (
        <div className="font-mono text-[11px] text-radar-red">{error}</div>
      )}
      {!error && notice && (
        <div className="font-mono text-[10px] text-radar-green">✓ {notice}</div>
      )}

      {sub.retry && (
        <div className="font-mono text-[11px] text-paper-dim">
          {sub.retry.waiting_for_new_card ? (
            <span className="text-radar-amber">
              Waiting for a new card — the last decline ({sub.retry.hard_decline_code}) means this
              card can&rsquo;t be charged again. A card the member adds is charged within 15
              minutes; one Account Updater replaces, by the next morning.
            </span>
          ) : sub.retry.next_retry_on ? (
            <span>Next retry {sub.retry.next_retry_on} · a card the member adds is charged within 15 minutes</span>
          ) : (
            <span>A retry is in progress</span>
          )}
          <span> · member keeps access through {sub.retry.access_until}</span>
        </div>
      )}

      {sub.attempts.length > 0 && (
        <table className="w-full text-[12px]">
          <thead>
            <tr className="border-b border-ink-500">
              <Th>Attempt</Th>
              <Th>Scheduled</Th>
              <Th>Status</Th>
              <Th>Amount</Th>
              <Th>Decline code</Th>
              <Th>Triggered by</Th>
            </tr>
          </thead>
          <tbody>
            {sub.attempts.map((a) => (
              <tr key={a.id} className="border-b border-ink-500/40">
                <Td>{a.attempt_no}</Td>
                <Td><span className="font-mono tabular-nums">{a.scheduled_for}</span></Td>
                <Td>
                  <span className={'label ' + (a.status === 'succeeded' ? 'text-radar-green'
                    : a.status === 'failed' ? 'text-radar-red' : 'text-radar-amber')}>
                    {a.status}
                  </span>
                </Td>
                <Td><span className="font-mono tabular-nums">{fmtMoney(a.amount_cents / 100, false, 2)}</span></Td>
                <Td><span className="font-mono text-paper-dim">{a.decline_code ?? '—'}</span></Td>
                <Td><span className="font-mono text-paper-dim">{triggeredByLabel(a.triggered_by)}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

/** What started an attempt, in support's words. */
function triggeredByLabel(t: string): string {
  if (t === 'scheduler') return 'schedule'
  if (t === 'card_changed') return 'new card'
  if (t === 'hard_decline_close') return 'no new card — ended'
  if (t.startsWith('admin:')) return 'charge now'
  return t
}

function CardExpiryCell({ card }: { card: CardOnFile | null }) {
  if (!card || !card.last4) {
    return <div className="font-mono text-[12px] text-radar-amber">No card on file</div>
  }
  const expired = !!card.expiry && card.expiry < todayStr().slice(0, 7)
  return (
    <div className={'font-mono text-[13px] tabular-nums ' + (expired ? 'text-radar-red' : 'text-paper')}>
      {card.brand ?? 'card'} ····{card.last4}
      {card.expiry && (
        <span className={expired ? '' : 'text-paper-trace'}> · exp {fmtCardExpiry(card.expiry)}</span>
      )}
    </div>
  )
}

// ---- Local helpers (kept private to this file) ---------------------

/**
 * The Expires cell. Read-only text for SimpleCirc-sourced rows — SC owns
 * those dates and its nightly import would revert anything typed here —
 * and a click-to-edit date field for memberships sold through our own
 * checkout. The backend enforces the same rule; `expiry_editable` only
 * decides whether we offer the affordance.
 */
function ExpiryCell(
  { subscriberId, entitlement }:
  { subscriberId: number; entitlement: EntitlementSnapshot },
) {
  const qc = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [value, setValue] = useState(entitlement.expires_at ?? '')
  const [reason, setReason] = useState('')

  const save = useMutation({
    mutationFn: () =>
      api.patch(
        `/api/admin/members/${subscriberId}/subscriptions/` +
        `${entitlement.subscription_id}/expiry`,
        { current_period_end: value || null, reason },
      ),
    onSuccess: () => {
      setEditing(false)
      setReason('')
      qc.invalidateQueries({ queryKey: ['admin-member', subscriberId] })
    },
  })

  const shown = (
    <span className="font-mono text-[12px] tabular-nums">
      {entitlement.expires_at ?? '—'}
    </span>
  )

  if (!entitlement.expiry_editable) return shown

  if (!editing) {
    return (
      <div className="flex items-center gap-2">
        {shown}
        <button
          type="button"
          className="label text-paper-trace hover:text-amber-glow"
          title={
            `Editable — sold via ${entitlement.subscription_source}, ` +
            'not synced from SimpleCirc'
          }
          onClick={() => {
            setValue(entitlement.expires_at ?? '')
            setReason('')
            save.reset()
            setEditing(true)
          }}
        >
          edit
        </button>
      </div>
    )
  }

  const err = save.error instanceof ApiError
    ? ((save.error.detail as { detail?: string })?.detail
        ?? save.error.message)
    : null

  return (
    <div className="space-y-2 py-1">
      <input
        type="date"
        value={value}
        onChange={(ev) => setValue(ev.target.value)}
        className="bg-ink-900 border border-ink-500 px-2 py-1
                   font-mono text-[12px] tabular-nums text-paper"
      />
      <input
        type="text"
        value={reason}
        placeholder="reason (required)"
        onChange={(ev) => setReason(ev.target.value)}
        className="block w-52 bg-ink-900 border border-ink-500 px-2 py-1
                   font-mono text-[11px] text-paper"
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!reason.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={save.isPending}
          onClick={() => setEditing(false)}
        >
          Cancel
        </Button>
      </div>
      {/* Clearing the date is deliberate, not a slip — say what it does. */}
      {!value && (
        <div className="font-mono text-[11px] text-radar-amber">
          empty = no expiry (open-ended access)
        </div>
      )}
      {err && (
        <div className="font-mono text-[11px] text-radar-red">{err}</div>
      )}
    </div>
  )
}


function KV({ k, v, tone }: { k: string; v: string; tone?: 'amber' }) {
  const valueClass =
    'font-mono text-[13px] tabular-nums ' +
    (tone === 'amber' ? 'text-radar-amber' : 'text-paper')
  return (
    <div className="flex justify-between gap-4">
      <span className="label text-paper-trace">{k}</span>
      <span className={valueClass}>{v}</span>
    </div>
  )
}
function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <th className={`px-4 py-3 label text-paper-trace text-${align}`}>
      {children}
    </th>
  )
}
function Td({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return <td className={`px-4 py-2 text-${align} text-paper-dim`}>{children}</td>
}
